import { type Editor } from '@tiptap/react';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Lib ---
import { isExtensionAvailable, isMarkInSchema, isNodeInSchema } from '@/lib/tiptap-utils';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Skeleton } from '@/components/ui/skeleton';

export type ExtensionType = 'node' | 'mark' | 'extension';

export interface GenericExtensionButtonProps<T = any> extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor | null;

    /**
     * The name of the extension (e.g., 'bold', 'multipleChoice', 'zydefn')
     */
    extensionName: string;

    /**
     * The type of extension
     */
    extensionType: ExtensionType;

    /**
     * The icon component to display
     */
    icon: LucideIcon;

    /**
     * The label for the button (used for aria-label and tooltip)
     */
    label: string;

    /**
     * Function to execute the command
     */
    command: (editor: Editor, options?: T) => void;

    /**
     * Default options to pass to the command
     */
    options?: T;

    /**
     * Optional text to display alongside the icon.
     */
    text?: string;

    /**
     * Optional function to check if the extension is active
     * If not provided, will use editor.isActive(extensionName)
     */
    isActive?: (editor: Editor) => boolean;

    /**
     * Optional function to check if the button should be disabled
     */
    isDisabled?: (editor: Editor) => boolean;

    /**
     * Optional function to determine if button should be shown
     */
    shouldShow?: (editor: Editor) => boolean;

    /**
     * Whether the button should hide when the extension is not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
}

export const GenericExtensionButton = React.forwardRef<HTMLButtonElement, GenericExtensionButtonProps>(
    <T,>(
        {
            editor: providedEditor,
            extensionName,
            extensionType,
            icon: Icon,
            label,
            command,
            options,
            text,
            isActive: customIsActive,
            isDisabled: customIsDisabled,
            shouldShow,
            hideWhenUnavailable = false,
            className = '',
            onClick,
            children,
            ...buttonProps
        }: GenericExtensionButtonProps<T>,
        ref: React.Ref<HTMLButtonElement>,
    ) => {
        const { editor } = useTiptapEditor(providedEditor);

        const subscribe = React.useCallback(
            (onStoreChange: () => void) => {
                if (!editor) return () => {};
                editor.on('selectionUpdate', onStoreChange);
                editor.on('update', onStoreChange);
                return () => {
                    editor.off('selectionUpdate', onStoreChange);
                    editor.off('update', onStoreChange);
                };
            },
            [editor],
        );

        const getIsActiveSnapshot = React.useCallback(
            () => (editor ? (customIsActive ? customIsActive(editor) : editor.isActive(extensionName)) : false),
            [editor, customIsActive, extensionName],
        );

        const getIsDisabledSnapshot = React.useCallback(
            () => (editor && customIsDisabled ? customIsDisabled(editor) : false),
            [editor, customIsDisabled],
        );

        const isActiveState = React.useSyncExternalStore(subscribe, getIsActiveSnapshot);
        const isDisabled = React.useSyncExternalStore(subscribe, getIsDisabledSnapshot);

        const buttonSkeleton = (
            <Skeleton>
                <Button
                    type="button"
                    data-style="ghost"
                    data-active-state="off"
                    role="button"
                    tabIndex={-1}
                >
                    <Icon className="tiptap-button-icon" />
                </Button>
            </Skeleton>
        );

        // Early returns after all hooks
        if (!editor || !editor.isEditable) {
            return buttonSkeleton;
        }

        // Check if extension is in schema
        const extensionInSchema =
            extensionType === 'node'
                ? isNodeInSchema(extensionName, editor)
                : extensionType === 'mark'
                  ? isMarkInSchema(extensionName, editor)
                  : isExtensionAvailable(editor, extensionName);

        if (!extensionInSchema) {
            return null;
        }

        // Custom visibility check
        if (shouldShow && !shouldShow(editor)) {
            return buttonSkeleton;
        }

        if (hideWhenUnavailable && isDisabled) {
            return buttonSkeleton;
        }

        const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            onClick?.(e);

            if (!e.defaultPrevented && !isDisabled) {
                command(editor, options);
            }
        };

        return (
            <Button
                type="button"
                className={className.trim()}
                disabled={isDisabled}
                data-style="ghost"
                data-active-state={isActiveState ? 'on' : 'off'}
                data-disabled={isDisabled}
                role="button"
                tabIndex={-1}
                aria-label={label}
                aria-pressed={isActiveState}
                tooltip={label}
                onClick={handleClick}
                {...buttonProps}
                ref={ref}
            >
                {children || (
                    <>
                        <Icon className="tiptap-button-icon" />
                        {text && <span className="tiptap-button-text">{text}</span>}
                    </>
                )}
            </Button>
        );
    },
);
